import React, { useState } from 'react';
import tournamentService from '../services/tournamentService';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { PiTrophy } from 'react-icons/pi';
import '../styles/AdminDashboard.css';

const CreateTournament = () => {
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        name: '',
        sportType: 'FOOTBALL',
        year: '',
        sportDbApiTournamentId: '',
        apiFootballId: '',
        apiFootballIdV3: '',
        apiFootballTeamId: '',
        international: false,
        activeForImport: false,
        startDate: '',
        endDate: '',
        overtimeEnabled: false,
        twoLegKnockout: false,
    });

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const payload = {
                name: form.name.trim(),
                sportType: form.sportType,
                year: form.year.trim() || null,
                sportDbApiTournamentId: form.sportDbApiTournamentId !== '' ? Number(form.sportDbApiTournamentId) : null,
                apiFootballId: form.apiFootballId !== '' ? Number(form.apiFootballId) : null,
                apiFootballIdV3: form.apiFootballIdV3 !== '' ? Number(form.apiFootballIdV3) : null,
                apiFootballTeamId: form.apiFootballTeamId !== '' ? Number(form.apiFootballTeamId) : null,
                international: form.international,
                activeForImport: form.activeForImport,
                startDate: form.startDate || null,
                endDate: form.endDate || null,
                overtimeEnabled: form.overtimeEnabled,
                twoLegKnockout: form.twoLegKnockout && form.overtimeEnabled,
            };
            await tournamentService.createTournament(payload);
            toast.success(`Turnaj "${payload.name}" byl úspěšně vytvořen`);
            navigate('/admin/dashboard');
        } catch (error) {
            const detail = error?.response?.data?.message || error?.response?.data || error?.message || 'Neznámá chyba';
            toast.error(`Nepodařilo se vytvořit turnaj: ${detail}`);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="admin-dashboard-container">
            <h2 className="admin-section-title">Nový turnaj</h2>
            <form onSubmit={handleSubmit} className="admin-action-card" style={{ maxWidth: 640 }}>
                <div className="admin-action-header">
                    <span className="admin-action-icon"><PiTrophy /></span>
                    <h3>Vytvořit turnaj</h3>
                </div>

                <div className="admin-action-body">
                    <div className="admin-field">
                        <label htmlFor="ct-name">Název *</label>
                        <input
                            id="ct-name"
                            className="admin-input"
                            type="text"
                            name="name"
                            value={form.name}
                            onChange={handleChange}
                            placeholder="např. Premier League"
                            maxLength={150}
                            required
                            disabled={submitting}
                        />
                    </div>

                    <div className="admin-field">
                        <label htmlFor="ct-sport">Sport *</label>
                        <select
                            id="ct-sport"
                            className="admin-select"
                            name="sportType"
                            value={form.sportType}
                            onChange={handleChange}
                            required
                            disabled={submitting}
                        >
                            <option value="FOOTBALL">Fotbal</option>
                            <option value="HOCKEY">Hokej</option>
                        </select>
                    </div>

                    <div className="admin-field">
                        <label htmlFor="ct-startDate">Začátek turnaje</label>
                        <input
                            id="ct-startDate"
                            className="admin-input"
                            type="date"
                            name="startDate"
                            value={form.startDate}
                            onChange={handleChange}
                            disabled={submitting}
                        />
                    </div>

                    <div className="admin-field">
                        <label htmlFor="ct-endDate">Konec turnaje</label>
                        <input
                            id="ct-endDate"
                            className="admin-input"
                            type="date"
                            name="endDate"
                            value={form.endDate}
                            onChange={handleChange}
                            disabled={submitting}
                        />
                    </div>

                    <div className="admin-field">
                        <label htmlFor="ct-year">Rok / sezona</label>
                        <input
                            id="ct-year"
                            className="admin-input"
                            type="text"
                            name="year"
                            value={form.year}
                            onChange={handleChange}
                            placeholder="např. 2024/2025"
                            maxLength={20}
                            disabled={submitting}
                        />
                    </div>

                    <div className="admin-field">
                        <label htmlFor="ct-apiFootballId">API-Football ID (v2)</label>
                        <input
                            id="ct-apiFootballId"
                            className="admin-input"
                            type="number"
                            name="apiFootballId"
                            value={form.apiFootballId}
                            onChange={handleChange}
                            placeholder="např. 39"
                            min="0"
                            disabled={submitting}
                        />
                    </div>

                    <div className="admin-field">
                        <label htmlFor="ct-apiFootballIdV3">API-Football ID (v3)</label>
                        <input
                            id="ct-apiFootballIdV3"
                            className="admin-input"
                            type="number"
                            name="apiFootballIdV3"
                            value={form.apiFootballIdV3}
                            onChange={handleChange}
                            placeholder="např. 39"
                            min="0"
                            disabled={submitting || form.apiFootballTeamId !== ''}
                        />
                    </div>

                    <div className="admin-field">
                        <label htmlFor="ct-apiFootballTeamId">API-Football ID týmu (týmový turnaj)</label>
                        <input
                            id="ct-apiFootballTeamId"
                            className="admin-input"
                            type="number"
                            name="apiFootballTeamId"
                            value={form.apiFootballTeamId}
                            onChange={handleChange}
                            placeholder="např. 604 (Sparta Praha)"
                            min="0"
                            disabled={submitting || form.apiFootballIdV3 !== ''}
                        />
                        <small style={{ color: '#6b7280' }}>
                            Týmový turnaj = všechny zápasy jednoho týmu v sezóně (liga, pohár, Evropa, přáteláky).
                            Jen pro fotbal; vyplňte buď ID soutěže (v3), nebo ID týmu — ne obojí.
                            Prodloužení se určuje automaticky podle soutěže a kola, checkboxy níže se ignorují.
                            Pro import je nutný rok/sezona a konec turnaje.
                        </small>
                    </div>

                    <div className="admin-field">
                        <label htmlFor="ct-sportDbId">TheSportsDB API ID</label>
                        <input
                            id="ct-sportDbId"
                            className="admin-input"
                            type="number"
                            name="sportDbApiTournamentId"
                            value={form.sportDbApiTournamentId}
                            onChange={handleChange}
                            placeholder="např. 4328"
                            min="0"
                            disabled={submitting}
                        />
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 4 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.9rem', color: '#374151' }}>
                            <input
                                type="checkbox"
                                name="international"
                                checked={form.international}
                                onChange={handleChange}
                                disabled={submitting}
                                style={{ width: 16, height: 16, cursor: 'pointer' }}
                            />
                            Mezinárodní turnaj
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.9rem', color: '#374151' }}>
                            <input
                                type="checkbox"
                                name="activeForImport"
                                checked={form.activeForImport}
                                onChange={handleChange}
                                disabled={submitting}
                                style={{ width: 16, height: 16, cursor: 'pointer' }}
                            />
                            Aktivní pro import zápasů (cron job)
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.9rem', color: '#374151' }}>
                            <input
                                type="checkbox"
                                name="overtimeEnabled"
                                checked={form.overtimeEnabled}
                                onChange={handleChange}
                                disabled={submitting}
                                style={{ width: 16, height: 16, cursor: 'pointer' }}
                            />
                            Prodloužení povoleno (overtime enabled)
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: form.overtimeEnabled ? 'pointer' : 'not-allowed', fontSize: '0.9rem', color: form.overtimeEnabled ? '#374151' : '#9ca3af' }}>
                            <input
                                type="checkbox"
                                name="twoLegKnockout"
                                checked={form.twoLegKnockout}
                                onChange={handleChange}
                                disabled={submitting || !form.overtimeEnabled}
                                style={{ width: 16, height: 16, cursor: form.overtimeEnabled ? 'pointer' : 'not-allowed' }}
                            />
                            Vyřazovací fáze na dva zápasy (two-leg knockout)
                        </label>
                    </div>
                </div>

                <button
                    type="submit"
                    className="admin-action-btn"
                    disabled={submitting || !form.name.trim()}
                >
                    {submitting ? 'Ukládám…' : <><PiTrophy style={{ marginRight: 6 }} /> Vytvořit turnaj</>}
                </button>
            </form>
        </div>
    );
};

export default CreateTournament;
